import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import initBootstrapComponents from '../../utils/bootstrap';
import '@splidejs/react-splide/css';
import 'bootstrap/dist/css/bootstrap.min.css';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { useBasket } from '../../context/BasketContext';
import axios from 'axios';
import { Riple } from "react-loading-indicators";
import { API_BASE_URL } from '../../constants/api';
import scooterIcon from '../../assets/img/icons/scooter.png';
import wheelchairIcon from '../../assets/img/icons/wheelchair.png';
import tileBackground from '../../assets/img/backgrounds/select-type-tile.png';
import { generateEtaTimeOptions, isValidEtaTime } from '../../utils/etaTimeOptions';

const ScooterBasketConfirmation = () => {
    const navigate = useNavigate();
    const { eventId, vehicleType, vehicleId } = useParams();
    const [searchParams] = useSearchParams();
    const slotIdParam = searchParams.get('slot');

    const { addToBasket, basket } = useBasket();

    const typeLabel = vehicleType === 'wheelchair' ? 'wheelchair' : 'scooter';
    const typeLabelCapitalised = typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1);
    const typeIcon = vehicleType === 'wheelchair' ? wheelchairIcon : scooterIcon;

    const [vehicle, setVehicle] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [eventDetails, setEventDetails] = useState(null);

    const [formData, setFormData] = useState({
        title: '',
        firstName: '',
        lastName: '',
        email: '',
        confirmEmail: '',
        telephone: '',
        isExistingCustomer: '',
        etaTime: '',
        over25Stone: ''
    });

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const etaTimeOptions = useMemo(
        () => generateEtaTimeOptions(selectedSlot?.start_at, selectedSlot?.end_at),
        [selectedSlot?.start_at, selectedSlot?.end_at]
    );

    useEffect(() => {
        initBootstrapComponents();
        fetchVehicleAndSlots();
        fetchEventDetails();
    }, []);

    useEffect(() => {
        if (error) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [error]);

    const fetchEventDetails = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/events/${eventId}/details?key=7678356c642c91a255471d162205e0f3`);
            setEventDetails(response.data.data);
        } catch (err) {
            console.error('Error fetching event details:', err);
        }
    };

    const fetchVehicleAndSlots = async () => {
        setLoading(true);
        try {
            const checkLimit = localStorage.getItem('ambassador_token') ? 'admin' : 'customer';
            const response = await axios.get(`${API_BASE_URL}/api/events/${eventId}/slots?key=7678356c642c91a255471d162205e0f3&format=vehicles&vehicle_type=${vehicleType}&check_limit=${checkLimit}`);
            Object.values(response.data.data).forEach(vehicleData => {
                if (vehicleData.vehicle.id == vehicleId) {
                    setVehicle(vehicleData.vehicle);

                    const slotsArray = Object.values(vehicleData.slots);
                    const matchedSlot = slotsArray.find(slot => String(slot.id) === String(slotIdParam));
                    if (matchedSlot) {
                        setSelectedSlot(matchedSlot);
                    }
                }
            });
        } catch (err) {
            console.error('Error fetching data:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-GB', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const handleRemoveSlot = () => {
        navigate(`/scooter/event/${eventId}/${vehicleType}/vehicles`);
    };

    const composeEtaAt = () => {
        if (!selectedSlot || !formData.etaTime) return '';
        const date = new Date(selectedSlot.start_at);
        const [hours, minutes] = formData.etaTime.split(':');
        date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
        const pad = (n) => String(n).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    };

    const lockSlot = async () => {
        const formDataObj = new FormData();
        const token = localStorage.getItem('ambassador_token');
        const ambassadorId = localStorage.getItem('ambassador_id');

        formDataObj.append('slots[0][slot]', parseInt(selectedSlot.id));
        formDataObj.append('slots[0][vehicle]', parseInt(vehicle.id));
        if (ambassadorId) {
            formDataObj.append('ambassador_id', ambassadorId);
        }
        formDataObj.append('key', '7678356c642c91a255471d162205e0f3');
        formDataObj.append('eta_at', composeEtaAt());
        formDataObj.append('over_25_stone', formData.over25Stone === 'yes' ? 1 : 0);
        formDataObj.append('source', token ? 'admin' : 'customer');

        const headers = {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'multipart/form-data'
        };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const resp = await axios.post(`${API_BASE_URL}/api/events/bookings/book`, formDataObj, {
            headers,
            withCredentials: true
        });

        const errorMessage = resp.data.data.errmessage;
        if (errorMessage) {
            setError(errorMessage);
            return null;
        }
        const bookingIds = resp.data.data.bookings.map(booking => booking.id);
        return bookingIds[0];
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedSlot || !vehicle) return;

        if (formData.email.trim().toLowerCase() !== formData.confirmEmail.trim().toLowerCase()) {
            setError('The email addresses you entered do not match. Please check and try again.');
            return;
        }

        if (!isValidEtaTime(formData.etaTime, selectedSlot?.start_at, selectedSlot?.end_at)) {
            setError('Please select a valid estimated arrival time during the event.');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            const existingSlots = basket?.slots || [];
            const participantName = `${formData.firstName} ${formData.lastName}`.trim();
            const slotAlreadyInBasket = existingSlots.some(slot => String(slot.id) === String(selectedSlot.id));

            if (slotAlreadyInBasket) {
                // The slot is already locked/booked (e.g. the user came back to edit their details).
                // Update the existing slot rather than creating a duplicate booking.
                const updatedSlots = existingSlots.map(slot =>
                    String(slot.id) === String(selectedSlot.id)
                        ? { ...slot, participant_name: participantName }
                        : slot
                );
                addToBasket({
                    slots: updatedSlots,
                    event_id: eventId,
                    event_name: eventDetails?.name,
                    event_start_at: eventDetails?.start_at,
                    event_end_at: eventDetails?.end_at,
                });
            } else {
                const bookingId = await lockSlot();
                if (!bookingId) {
                    setSubmitting(false);
                    return;
                }

                const newSlot = {
                    id: selectedSlot.id,
                    booking_id: bookingId,
                    vehicle: vehicle,
                    vehicle_type: vehicleType,
                    start_at: selectedSlot.start_at,
                    date: new Date(selectedSlot.start_at),
                    eta_at: composeEtaAt(),
                    over_25_stone: formData.over25Stone === 'yes' ? 1 : 0,
                    participant_name: participantName
                };

                addToBasket({
                    slots: [...existingSlots, newSlot],
                    event_id: eventId,
                    event_name: eventDetails?.name,
                    event_start_at: eventDetails?.start_at,
                    event_end_at: eventDetails?.end_at,
                });
            }

            const customerDetails = {
                email: formData.email,
                title: formData.title,
                firstName: formData.firstName,
                lastName: formData.lastName,
                telephone: formData.telephone,
                addressLine1: '',
                addressLine2: '',
                houseNumber: '',
                postcode: '',
                isExistingCustomer: formData.isExistingCustomer
            };
            localStorage.setItem('customerDetails', JSON.stringify(customerDetails));

            navigate('/scooter-checkout');
        } catch (err) {
            console.error('Booking error:', err);
            const apiMessage = err.response?.data?.data?.message;
            setError(apiMessage || 'An error occurred while creating your booking');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex flex-column justify-content-between min-vh-100">
                <Header />
                <div className="d-flex justify-content-center align-items-center flex-grow-1">
                    <Riple color="#0052BE" size="medium" />
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="d-flex flex-column justify-content-between min-vh-100" id="body-container">
            <Header />

            <main>
                <section className="bg-motability-mid-black-5 py-32 pt-lg-96 pb-lg-148" id="book-vehicle">
                    <div className="container">
                        <h1 className="text-primary mb-4 mb-lg-5">
                            Customer details
                        </h1>

                        {error && (
                            <div className="alert alert-danger mb-4" role="alert">
                                {error}
                            </div>
                        )}

                        {!selectedSlot ? (
                            <div className="bg-motability-white border border-motability-mid-black-25 rounded-4 p-4 p-lg-32">
                                <p className="mb-0 text-capitalize">
                                    We couldn't find the selected date for this {typeLabel}. Please go back and choose a date.
                                </p>
                            </div>
                        ) : (
                            <div className="row row-gap-4 row-gap-lg-0 gx-xxl-5">
                                <div className="col-lg-6">
                                    <div className="bg-motability-white border border-motability-mid-black-25 rounded-4 p-4 p-lg-32 pt-lg-28">
                                        <h5 className="text-primary fw-semibold mb-4 mb-lg-32">
                                            Please enter your details below
                                        </h5>

                                        <form onSubmit={handleSubmit} className="custom" id="book-a-scooter-form">
                                            <fieldset>
                                                <div className="row gx-xxl-32">
                                                    <div className="form-group col-lg-5 col-xl-4 mb-3">
                                                        <label htmlFor="book-a-scooter-title" className="small ms-1 mb-1">
                                                            Title
                                                        </label>
                                                        <select
                                                            name="title"
                                                            id="book-a-scooter-title"
                                                            className="form-select f-input-lg blue-select-dropdown border-motability-mid-black-40 shadow-motability-mid-black-12-inset"
                                                            value={formData.title}
                                                            onChange={handleInputChange}
                                                        >
                                                            <option value="" disabled>-Select-</option>
                                                            <option value="mr">Mr</option>
                                                            <option value="mrs">Mrs</option>
                                                            <option value="miss">Miss</option>
                                                            <option value="ms">Ms</option>
                                                            <option value="dr">Dr</option>
                                                        </select>
                                                    </div>

                                                    <div className="form-group col-12 mb-3">
                                                        <div className="row">
                                                            <div className="col-md-6">
                                                                <label htmlFor="book-a-scooter-first-name" className="small ms-1 mb-1">
                                                                    First name *
                                                                </label>
                                                                <input
                                                                    name="firstName"
                                                                    id="book-a-scooter-first-name"
                                                                    type="text"
                                                                    className="form-control f-input-lg border-motability-mid-black-40 shadow-motability-mid-black-12-inset"
                                                                    required
                                                                    value={formData.firstName}
                                                                    onChange={handleInputChange}
                                                                />
                                                            </div>
                                                            <div className="col-md-6">
                                                                <label htmlFor="book-a-scooter-last-name" className="small ms-1 mb-1">
                                                                    Last name *
                                                                </label>
                                                                <input
                                                                    name="lastName"
                                                                    id="book-a-scooter-last-name"
                                                                    type="text"
                                                                    className="form-control f-input-lg border-motability-mid-black-40 shadow-motability-mid-black-12-inset"
                                                                    required
                                                                    value={formData.lastName}
                                                                    onChange={handleInputChange}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="form-group mb-3">
                                                        <label htmlFor="book-a-scooter-email-address" className="small ms-1 mb-1">
                                                            Email address *
                                                        </label>
                                                        <input
                                                            name="email"
                                                            id="book-a-scooter-email-address"
                                                            type="email"
                                                            className="form-control f-input-lg border-motability-mid-black-40 shadow-motability-mid-black-12-inset"
                                                            required
                                                            value={formData.email}
                                                            onChange={handleInputChange}
                                                        />
                                                    </div>

                                                    <div className="form-group mb-3">
                                                        <label htmlFor="book-a-scooter-confirm-email-address" className="small ms-1 mb-1">
                                                            Confirm email address *
                                                        </label>
                                                        <input
                                                            name="confirmEmail"
                                                            id="book-a-scooter-confirm-email-address"
                                                            type="email"
                                                            className="form-control f-input-lg border-motability-mid-black-40 shadow-motability-mid-black-12-inset"
                                                            required
                                                            onPaste={(e) => e.preventDefault()}
                                                            value={formData.confirmEmail}
                                                            onChange={handleInputChange}
                                                        />
                                                    </div>

                                                    <div className="form-group mb-3">
                                                        <label htmlFor="book-a-scooter-telephone-number" className="small ms-1 mb-1">
                                                            Telephone number*
                                                        </label>
                                                        <input
                                                            name="telephone"
                                                            id="book-a-scooter-telephone-number"
                                                            type="tel"
                                                            className="form-control f-input-lg border-motability-mid-black-40 shadow-motability-mid-black-12-inset"
                                                            required
                                                            value={formData.telephone}
                                                            onChange={handleInputChange}
                                                        />
                                                    </div>

                                                    <div className="form-group mb-4 mb-lg-32">
                                                        <label htmlFor="book-a-scooter-eta-at" className="text-primary fw-semibold mb-3 d-block">
                                                            Estimated arrival time *
                                                        </label>
                                                        <select
                                                            name="etaTime"
                                                            id="book-a-scooter-eta-at"
                                                            className="form-select f-input-lg blue-select-dropdown border-motability-mid-black-40 shadow-motability-mid-black-12-inset w-auto"
                                                            required
                                                            value={formData.etaTime}
                                                            onChange={handleInputChange}
                                                        >
                                                            <option value="" disabled>-Select-</option>
                                                            {etaTimeOptions.map(({ value, label }) => (
                                                                <option key={value} value={value}>{label}</option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    <div className="form-group mb-4 mb-lg-40">
                                                        <p className="text-primary fw-semibold mb-1">
                                                            Will the person using the {typeLabel} weigh over 25 stone?
                                                        </p>
                                                        <p className="small text-motability-black mb-3">
                                                            We can only accommodate up to a maximum permitted weight of 28 stone.
                                                        </p>

                                                        <div className="d-flex align-items-center gap-5">
                                                            <div className="form-check d-flex align-items-center mb-0">
                                                                <input
                                                                    name="over25Stone"
                                                                    id="over-25-stone-yes"
                                                                    type="radio"
                                                                    className="form-check-input cursor-pointer mt-0"
                                                                    value="yes"
                                                                    checked={formData.over25Stone === 'yes'}
                                                                    onChange={handleInputChange}
                                                                    required
                                                                />
                                                                <label htmlFor="over-25-stone-yes" className="form-check-label cursor-pointer ps-12">
                                                                    Yes
                                                                </label>
                                                            </div>

                                                            <div className="form-check d-flex align-items-center mb-0">
                                                                <input
                                                                    name="over25Stone"
                                                                    id="over-25-stone-no"
                                                                    type="radio"
                                                                    className="form-check-input cursor-pointer mt-0"
                                                                    value="no"
                                                                    checked={formData.over25Stone === 'no'}
                                                                    onChange={handleInputChange}
                                                                    required
                                                                />
                                                                <label htmlFor="over-25-stone-no" className="form-check-label cursor-pointer ps-12">
                                                                    No
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="form-group mb-4 mb-lg-40">
                                                        <p className="text-primary fw-semibold">
                                                            Are you an existing Motability Scheme customer?
                                                        </p>

                                                        <div className="d-flex align-items-center gap-5">
                                                            <div className="form-check d-flex align-items-center mb-0">
                                                                <input
                                                                    name="isExistingCustomer"
                                                                    id="book-a-scooter-is-existing-customer"
                                                                    type="radio"
                                                                    className="form-check-input cursor-pointer mt-0"
                                                                    value="yes"
                                                                    checked={formData.isExistingCustomer === 'yes'}
                                                                    onChange={handleInputChange}
                                                                    required
                                                                />
                                                                <label htmlFor="book-a-scooter-is-existing-customer" className="form-check-label cursor-pointer ps-12">
                                                                    Yes
                                                                </label>
                                                            </div>

                                                            <div className="form-check d-flex align-items-center mb-0">
                                                                <input
                                                                    name="isExistingCustomer"
                                                                    id="book-a-scooter-is-not-existing-customer"
                                                                    type="radio"
                                                                    className="form-check-input cursor-pointer mt-0"
                                                                    value="no"
                                                                    checked={formData.isExistingCustomer === 'no'}
                                                                    onChange={handleInputChange}
                                                                    required
                                                                />
                                                                <label htmlFor="book-a-scooter-is-not-existing-customer" className="form-check-label cursor-pointer ps-12">
                                                                    No
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <button type="submit" className="btn btn-primary px-29 d-flex align-items-center justify-content-center gap-2" disabled={submitting}>
                                                            {submitting ? (
                                                                <>
                                                                    <Riple color="#FFFFFF" size="small" />
                                                                    <span>Processing...</span>
                                                                </>
                                                            ) : (
                                                                'Continue to checkout'
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>
                                            </fieldset>
                                        </form>
                                    </div>
                                </div>

                                <div className="col-lg-6">
                                    <div className="bg-motability-mid-black-5 border border-motability-mid-black-2 rounded-4 p-4 p-lg-32">
                                        <h5 className="text-primary mb-3">
                                            Your order
                                        </h5>

                                        <div className="order-items">
                                            <div className="order-item py-3 d-flex align-items-center gap-3">
                                                <div
                                                    className="rounded-3 overflow-hidden d-flex align-items-center justify-content-center flex-shrink-0"
                                                    style={{
                                                        width: '120px',
                                                        height: '120px',
                                                        backgroundImage: `url(${tileBackground})`,
                                                        backgroundSize: 'cover',
                                                        backgroundPosition: 'center',
                                                        backgroundRepeat: 'no-repeat'
                                                    }}
                                                >
                                                    <img
                                                        src={typeIcon}
                                                        alt=""
                                                        aria-hidden="true"
                                                        style={{ width: '60%', height: 'auto' }}
                                                    />
                                                </div>
                                                <div className="d-flex flex-column">
                                                    <p className="fw-semibold mb-0">
                                                        {eventDetails?.name}
                                                    </p>
                                                    <p className="mb-0">
                                                        <span className="fw-semibold">Booking:</span> {typeLabelCapitalised}
                                                    </p>
                                                    <p className="fw-semibold mb-0">
                                                        {vehicle?.manufacturer?.name} {vehicle?.model || vehicle?.name}
                                                    </p>
                                                    <p className="fw-semibold mb-0">
                                                        {formatDate(selectedSlot.start_at)}
                                                    </p>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger ms-auto align-self-start"
                                                    title="Remove this slot"
                                                    aria-label="Remove this slot"
                                                    onClick={handleRemoveSlot}
                                                >
                                                    &times;
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="d-flex flex-column align-items-start mt-4 mt-lg-64">
                            <button
                                onClick={() => navigate(`/scooter/event/${eventId}/${vehicleType}/vehicles`)}
                                className="btn btn-link p-0 text-decoration-none"
                            >
                                ‹ <span className="text-decoration-underline">Back to date selection</span>
                            </button>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
};

export default ScooterBasketConfirmation;
